'use client';

import { Anchor } from '@/components/ui/anchor';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

type MembershipOptionProps = {
  actionLabel: string;
  dataTestPrefix: string;
  description: string;
  href: string;
  imageAlt: string;
  imageSrc: string;
  learnMoreHref: string;
  onAction: (href: string) => void;
  trackingName: string;
};

const MembershipOption = ({
  actionLabel,
  dataTestPrefix,
  description,
  href,
  imageAlt,
  imageSrc,
  learnMoreHref,
  onAction,
  trackingName,
}: Readonly<MembershipOptionProps>): React.JSX.Element => {
  const { trackClick } = useDataAnalytics();

  const handleAction = (): void => {
    trackClick({
      linkCategory: AnalyticsPageCategories.MOVIE_REWARDS,
      linkName: trackingName,
    });
    onAction(href);
  };

  return (
    <div className="flex flex-col items-center gap-ms text-center">
      <Image priority
        alt={imageAlt}
        className="h-auto w-full max-w-[130px] md:max-w-[210px]"
        height={75}
        src={imageSrc}
        width={295}
      />
      <p className="font-bold"
        data-test={`text-${dataTestPrefix}-description`}
      >
        {description}
      </p>
      <Button className="w-full max-w-[150px] md:max-w-[210px]"
        data-test={`button-${dataTestPrefix}-action`}
        onClick={handleAction}
      >
        {actionLabel}
      </Button>
      <Anchor data-test={`link-${dataTestPrefix}-learn-more`}
        dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
        dataLinkName={`${trackingName} Learn More`}
        href={learnMoreHref}
        variant="link"
      >
        Learn More
      </Anchor>
    </div>
  );
};

export const MovieRewardsJoinOrUpgrade = (): React.JSX.Element => {
  const router = useRouter();

  return (
    <section className="container-xl grid grid-cols-[1fr_1px_1fr] py-lg"
      data-test="section-movie-rewards-join-or-upgrade"
    >
      <div className="flex justify-center sm:px-lg">
        <MembershipOption actionLabel="Join for Free"
          dataTestPrefix="movie-fan"
          description="Earn points and get rewards"
          href="/membership/register?returnurl=/movie-rewards-info"
          imageAlt="Cinemark Movie Fan"
          imageSrc="/img/movie-rewards/Movie_Fan_Logo.png"
          learnMoreHref="/movie-rewards-info/movie-fan"
          trackingName="Movie Fan Join for Free"
          onAction={router.push}
        />
      </div>
      <Separator orientation="vertical" />
      <div className="flex justify-center sm:px-lg">
        <MembershipOption actionLabel="Upgrade"
          dataTestPrefix="movie-club"
          description="Exclusive member savings"
          href="/movieclub"
          imageAlt="Cinemark Movie Club"
          imageSrc="/img/movie-rewards/Movie_Club_Logo.png"
          learnMoreHref="/movieclub"
          trackingName="Movie Club Upgrade"
          onAction={router.push}
        />
      </div>
      <p className="col-span-3 mt-sm text-center"
        data-test="text-movie-rewards-sign-in"
      >
        Already have an account?{' '}
        <Anchor data-test="link-movie-rewards-sign-in"
          dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
          dataLinkName="Movie Rewards Sign In"
          href="/membership/signin?returnurl=/subscriptions"
          variant="link"
        >
          Sign in
        </Anchor>
      </p>
    </section>
  );
};
